import type RoomObsolete from '../../offices/models/room/room-obsolete';

/**
 * The desks that no longer exist for the days being booked, so the plan should not draw them.
 *
 * A retired desk is already refused by the server for those dates, and it already comes back in
 * `unavailableWorkplaceIds` — but "unavailable" renders as *booked*, which tells the person
 * looking at the plan that a colleague has the seat. They do not: the desk is gone. Omitting the
 * dot is the only honest answer (DESK_RETIREMENT_PLAN.md 2.1: the geometry stays, the rendering
 * changes).
 *
 * Hidden when the desk is retired on **any** of the selected dates. A booking made across three
 * days needs the desk on all three, so a desk that disappears halfway through cannot serve the
 * request — offering it and having the server refuse the second day is worse than not offering it.
 */
export const retiredWorkplaceIdsFor = (
  rooms: readonly RoomObsolete[] | undefined,
  dates: readonly Date[],
): string[] => {
  if (!rooms || dates.length === 0) return [];

  // Local calendar dates, because that is what the date picker chose and what the request will
  // carry. Comparing `yyyy-MM-dd` strings is exact and avoids a Date per desk per date.
  const asDay = (date: Date): string => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

  const days = dates.map(asDay);

  return rooms.flatMap((room) => (room.workplaces ?? [])
    .filter((workplace) => !!workplace.id && !!workplace.retiresOn
      // The retirement date is itself the first unbookable day.
      && days.some((day) => (workplace.retiresOn as string) <= day))
    .map((workplace) => workplace.id as string));
};

export default retiredWorkplaceIdsFor;
